import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUserKey } from '@/utils/filtersStorage';
import { useAuth } from '@/store/hooks/useAuth';
import type { AnalyticsConfig } from './analyticsConfig';
import type { AssociationRow } from './analyticsTypes';
import { getChartableByField, getChartableColumns, type ChartableColumn } from './chartableColumns';
import { resolveColumnChartType } from './columnChartTypes';
import { getMetricCatalog, getMetricByField, type MetricDefinition } from './metricCatalog';
import {
  buildDefaultLayout,
  buildColumnChartId,
  createColumnChartPanel,
  getPanelDefinition,
  isPresetPanelType,
  MAX_COLUMN_CHARTS,
  type ColumnChartParams,
  type DashboardLayoutState,
  type MetricScatterParams,
  type PanelParams,
  type PanelWidgetInstance,
  type PanelWidgetType,
  type ScoreDistributionParams,
  type SummaryCardInstance,
  type TopEntitiesParams,
} from './widgetRegistry';

const EMPTY_ROWS: AssociationRow[] = [];
const STORAGE_PREFIX = 'disgenet:dashboard-layout:v1';

function storageKey(userId: string, tab: string, view: string): string {
  return `${STORAGE_PREFIX}:${userId}:${tab}:${view}`;
}

function readStoredLayout(key: string): DashboardLayoutState | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DashboardLayoutState;
    if (
      !parsed ||
      (parsed.version !== 1 && parsed.version !== 2) ||
      !Array.isArray(parsed.panels) ||
      !Array.isArray(parsed.cards)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredLayout(key: string, layout: DashboardLayoutState) {
  try {
    localStorage.setItem(key, JSON.stringify(layout));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function isTopEntitiesParams(params: PanelParams): params is TopEntitiesParams {
  return 'metric' in params && !('xMetric' in params) && !('field' in params);
}

function isScatterParams(params: PanelParams): params is MetricScatterParams {
  return 'xMetric' in params && 'yMetric' in params;
}

function isDistributionParams(params: PanelParams): params is ScoreDistributionParams {
  return 'metric' in params && !('xMetric' in params) && !('field' in params);
}

function isColumnChartParams(params: PanelParams): params is ColumnChartParams {
  return 'field' in params;
}

function reconcilePanel(
  panel: PanelWidgetInstance,
  config: AnalyticsConfig,
  catalog: MetricDefinition[],
  chartableColumns: ChartableColumn[],
  keepUnknownColumns: boolean,
): PanelWidgetInstance | null {
  if (!panel?.type) return null;

  const definition = getPanelDefinition(panel.type);
  if (!definition?.isAvailable(config)) return null;

  if (panel.type === 'columnChart') {
    if (!isColumnChartParams(panel.params)) return null;
    const { field } = panel.params;
    const column = getChartableByField(chartableColumns, field);

    if (!column && !keepUnknownColumns) return null;
    return {
      id: panel.id || buildColumnChartId(column?.field ?? field),
      type: 'columnChart',
      hidden: Boolean(panel.hidden),
      params: {
        field: column?.field ?? field,
        chartType: resolveColumnChartType(panel.params.chartType, column?.kind),
      },
    };
  }

  const defaults = definition.defaultParams(config, catalog) as PanelParams;
  let params = { ...defaults, ...panel.params } as PanelParams;

  if (panel.type === 'topEntities' && isTopEntitiesParams(params)) {
    const metric = getMetricByField(catalog, params.metric);
    params = { metric: metric?.field ?? (defaults as TopEntitiesParams).metric };
  }

  if (panel.type === 'scoreDistribution' && isDistributionParams(params)) {
    const metric = getMetricByField(catalog, params.metric);
    params = { metric: metric?.field ?? (defaults as ScoreDistributionParams).metric };
  }

  if (panel.type === 'metricScatter' && isScatterParams(params)) {
    const xMetric = getMetricByField(catalog, params.xMetric);
    const yMetric = getMetricByField(catalog, params.yMetric);
    const defaultsScatter = defaults as MetricScatterParams;
    const chartType =
      params.chartType === 'horizontalBar' ||
      params.chartType === 'verticalBar' ||
      params.chartType === 'scatter'
        ? params.chartType
        : 'scatter';
    params = {
      xMetric: xMetric?.field ?? defaultsScatter.xMetric,
      yMetric: yMetric?.field ?? defaultsScatter.yMetric,
      chartType,
    };
  }

  return {
    id: panel.id || panel.type,
    type: panel.type,
    hidden: Boolean(panel.hidden),
    params,
  };
}

export function reconcileLayout(
  stored: DashboardLayoutState | null,
  config: AnalyticsConfig,
  catalog: MetricDefinition[],
  chartableColumns: ChartableColumn[] = getChartableColumns(config, 'DISEASES'),
  options: { keepUnknownColumns?: boolean } = {},
): DashboardLayoutState {
  const defaults = buildDefaultLayout(config, catalog);

  if (!stored) return { ...defaults, version: 2 };

  const reconciledPanels: PanelWidgetInstance[] = [];
  const seenPresetTypes = new Set<PanelWidgetType>();
  const seenColumnFields = new Set<string>();

  stored.panels.forEach(panel => {
    const next = reconcilePanel(
      panel,
      config,
      catalog,
      chartableColumns,
      Boolean(options.keepUnknownColumns),
    );
    if (!next) return;

    if (isPresetPanelType(next.type)) {
      if (seenPresetTypes.has(next.type)) return;
      seenPresetTypes.add(next.type);
      reconciledPanels.push(next);
      return;
    }

    const field = (next.params as ColumnChartParams).field;
    if (!field || seenColumnFields.has(field)) return;
    if (seenColumnFields.size >= MAX_COLUMN_CHARTS) return;
    seenColumnFields.add(field);
    reconciledPanels.push(next);
  });

  const dismissedPresets = (stored.dismissedPresets ?? []).filter(isPresetPanelType);

  defaults.panels.forEach(panel => {
    if (seenPresetTypes.has(panel.type)) return;
    if (dismissedPresets.includes(panel.type)) return;
    reconciledPanels.push(panel);
  });

  const cardById = new Map(stored.cards.map(card => [card.id, card]));
  const cards: SummaryCardInstance[] = defaults.cards.map(defaultCard => {
    const storedCard = cardById.get(defaultCard.id);
    if (!storedCard) return defaultCard;
    const metric =
      storedCard.metric && getMetricByField(catalog, storedCard.metric) ? storedCard.metric : undefined;
    return {
      id: defaultCard.id,
      hidden: Boolean(storedCard.hidden),
      ...(metric ? { metric } : {}),
    };
  });

  return {
    version: 2,
    cards,
    panels: reconciledPanels,
    ...(dismissedPresets.length ? { dismissedPresets } : {}),
  };
}

function layoutsEqual(a: DashboardLayoutState, b: DashboardLayoutState): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useDashboardLayout(
  config: AnalyticsConfig | null,
  view: string,
  data: AssociationRow[] = EMPTY_ROWS,
) {
  const { user, isAuthenticated } = useAuth();
  const userId = useMemo(() => {
    if (!isAuthenticated || !user) return 'anonymous';
    return getUserKey(user) || 'anonymous';
  }, [isAuthenticated, user]);

  const catalog = useMemo(() => (config ? getMetricCatalog(config, view) : []), [config, view]);

  const chartableColumns = useMemo(
    () => (config ? getChartableColumns(config, view, data) : []),
    [config, view, data],
  );

  const key = useMemo(() => (config ? storageKey(userId, config.tab, view) : ''), [config, userId, view]);

  const defaultLayout = useMemo(
    () => (config ? { ...buildDefaultLayout(config, catalog), version: 2 as const } : null),
    [catalog, config],
  );

  const [layout, setLayout] = useState<DashboardLayoutState | null>(null);

  useEffect(() => {
    if (!config || !key) {
      setLayout(null);
      return;
    }
    setLayout(
      reconcileLayout(readStoredLayout(key), config, catalog, chartableColumns, {
        keepUnknownColumns: data.length === 0,
      }),
    );
  }, [catalog, chartableColumns, config, data.length, key]);

  const activeLayout = layout ?? defaultLayout;

  const persist = useCallback(
    (next: DashboardLayoutState) => {
      const withVersion: DashboardLayoutState = { ...next, version: 2 };
      setLayout(withVersion);
      if (key) writeStoredLayout(key, withVersion);
    },
    [key],
  );

  const isCustomized = Boolean(activeLayout && defaultLayout && !layoutsEqual(activeLayout, defaultLayout));

  const visiblePanels = useMemo(
    () => (activeLayout?.panels ?? []).filter(panel => !panel.hidden),
    [activeLayout],
  );

  const visibleCards = useMemo(
    () => (activeLayout?.cards ?? []).filter(card => !card.hidden),
    [activeLayout],
  );

  const addedColumnFields = useMemo(() => {
    const fields = new Set<string>();
    (activeLayout?.panels ?? []).forEach(panel => {
      if (panel.type !== 'columnChart') return;
      const field = (panel.params as ColumnChartParams).field;
      if (field) fields.add(field);
    });
    return fields;
  }, [activeLayout]);

  const columnChartCount = addedColumnFields.size;

  const togglePanel = useCallback(
    (panelId: string) => {
      if (!activeLayout) return;
      persist({
        ...activeLayout,
        panels: activeLayout.panels.map(panel =>
          panel.id === panelId ? { ...panel, hidden: !panel.hidden } : panel,
        ),
      });
    },
    [activeLayout, persist],
  );

  const toggleCard = useCallback(
    (cardId: string) => {
      if (!activeLayout) return;
      persist({
        ...activeLayout,
        cards: activeLayout.cards.map(card =>
          card.id === cardId ? { ...card, hidden: !card.hidden } : card,
        ),
      });
    },
    [activeLayout, persist],
  );

  const movePanel = useCallback(
    (panelId: string, direction: 'up' | 'down') => {
      if (!activeLayout) return;
      const index = activeLayout.panels.findIndex(panel => panel.id === panelId);
      if (index < 0) return;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= activeLayout.panels.length) return;
      const panels = [...activeLayout.panels];
      const [item] = panels.splice(index, 1);
      panels.splice(target, 0, item);
      persist({ ...activeLayout, panels });
    },
    [activeLayout, persist],
  );

  const reorderPanel = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!activeLayout) return;
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= activeLayout.panels.length ||
        toIndex >= activeLayout.panels.length ||
        fromIndex === toIndex
      ) {
        return;
      }
      const panels = [...activeLayout.panels];
      const [item] = panels.splice(fromIndex, 1);
      panels.splice(toIndex, 0, item);
      persist({ ...activeLayout, panels });
    },
    [activeLayout, persist],
  );

  const updatePanelParams = useCallback(
    (panelId: string, params: Partial<PanelParams>) => {
      if (!activeLayout) return;
      persist({
        ...activeLayout,
        panels: activeLayout.panels.map(panel => {
          if (panel.id !== panelId) return panel;
          return {
            ...panel,
            params: { ...panel.params, ...params } as PanelParams,
          };
        }),
      });
    },
    [activeLayout, persist],
  );

  const updateCardMetric = useCallback(
    (cardId: string, metric: string | undefined) => {
      if (!activeLayout) return;
      persist({
        ...activeLayout,
        cards: activeLayout.cards.map(card => {
          if (card.id !== cardId) return card;
          if (!metric) return { id: card.id, hidden: card.hidden };
          return { ...card, metric };
        }),
      });
    },
    [activeLayout, persist],
  );

  const addColumnChart = useCallback(
    (field: string): boolean => {
      if (!activeLayout || !config) return false;
      const column = getChartableByField(chartableColumns, field);
      if (!column) return false;
      if (addedColumnFields.has(column.field)) return false;
      if (columnChartCount >= MAX_COLUMN_CHARTS) return false;

      const panel = createColumnChartPanel(column);
      persist({
        ...activeLayout,
        panels: [...activeLayout.panels, panel],
      });
      return true;
    },
    [activeLayout, addedColumnFields, chartableColumns, columnChartCount, config, persist],
  );

  const addColumnCharts = useCallback(
    (fields: string[]): number => {
      if (!activeLayout || !config || !fields.length) return 0;
      let nextPanels = [...activeLayout.panels];
      let added = 0;
      const used = new Set(addedColumnFields);

      fields.forEach(field => {
        if (used.size >= MAX_COLUMN_CHARTS) return;
        const column = getChartableByField(chartableColumns, field);
        if (!column || used.has(column.field)) return;
        used.add(column.field);
        nextPanels = [...nextPanels, createColumnChartPanel(column)];
        added += 1;
      });

      if (!added) return 0;
      persist({ ...activeLayout, panels: nextPanels });
      return added;
    },
    [activeLayout, addedColumnFields, chartableColumns, config, persist],
  );

  const removePanel = useCallback(
    (panelId: string) => {
      if (!activeLayout) return;
      const panel = activeLayout.panels.find(entry => entry.id === panelId);
      if (!panel) return;

      const nextPanels = activeLayout.panels.filter(entry => entry.id !== panelId);
      if (isPresetPanelType(panel.type)) {
        const dismissed = new Set(activeLayout.dismissedPresets ?? []);
        dismissed.add(panel.type);
        persist({
          ...activeLayout,
          panels: nextPanels,
          dismissedPresets: [...dismissed],
        });
        return;
      }

      persist({
        ...activeLayout,
        panels: nextPanels,
      });
    },
    [activeLayout, persist],
  );

  const resetLayout = useCallback(() => {
    if (!defaultLayout) return;
    persist(defaultLayout);
  }, [defaultLayout, persist]);

  return {
    catalog,
    chartableColumns,
    layout: activeLayout,
    defaultLayout,
    visiblePanels,
    visibleCards,
    isCustomized,
    addedColumnFields,
    columnChartCount,
    maxColumnCharts: MAX_COLUMN_CHARTS,
    togglePanel,
    toggleCard,
    movePanel,
    reorderPanel,
    updatePanelParams,
    updateCardMetric,
    addColumnChart,
    addColumnCharts,
    removePanel,
    resetLayout,
  };
}
